import { Tag } from 'antd'
import { ExclamationCircleOutlined, RobotOutlined } from '@ant-design/icons'
import type { ManagerStepValue } from '../ManagerStep'
import type { ConfigValue } from '../ConfigStep'
import type { StrategyParam } from '@/types'

interface Props {
    managerStep: ManagerStepValue
    configStep: ConfigValue
}

const positionSizingLabel: Record<string, string> = {
    equal_weight: '等权分配',
    market_cap:   '市值加权',
    risk_parity:  '风险平价',
}

const rebalanceFrequencyLabel: Record<string, string> = {
    daily: '每日',
    weekly: '每周',
    monthly: '每月',
}

const benchmarkLabel: Record<string, string> = {
    HSI:     '恒生指数',
    HSCEI:   '恒生中企',
    HSTECH:  '恒生科技',
    CSI300:  '沪深 300',
    CSI500:  '中证 500',
    MSCI_CN: 'MSCI 中国',
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="review-step__row">
            <span className="review-step__row-label">{label}</span>
            <span className="review-step__row-value">{value}</span>
        </div>
    )
}

function KV({ label, value, accent }: { label: string; value: React.ReactNode; accent?: boolean }) {
    return (
        <div className="review-step__kv">
            <div className="review-step__kv-label">{label}</div>
            <div className={`review-step__kv-value${accent ? ' is-accent' : ''}`}>{value}</div>
        </div>
    )
}

function formatParam(p: StrategyParam): string {
    const unit = p.unit ? p.unit : ''
    if (p.type === 'range') {
        const lo = p.min ?? '-'
        const hi = p.max ?? '-'
        return `${lo} ~ ${hi}${unit ? ' ' + unit : ''}`
    }
    if (p.type === 'min') return `≥ ${p.min ?? '-'}${unit ? ' ' + unit : ''}`
    if (p.type === 'max') return `≤ ${p.max ?? '-'}${unit ? ' ' + unit : ''}`
    return '-'
}

export default function ReviewStep({ managerStep, configStep }: Props) {
    const { manager, strategyVersion, tradeType } = managerStep
    const isLive = tradeType === 'live'

    return (
        <section className="review-step">

            {isLive && (
                <div className="review-step__notice">
                    <ExclamationCircleOutlined style={{ marginRight: 8, color: '#f59e0b' }} />
                    实盘组合提交后将进入<strong>投资审核</strong>流程，审核通过后方可开始运营。
                </div>
            )}

            {/* 组合基本信息 */}
            <div className="review-step__section">
                <div className="review-step__section-title">组合基本信息</div>
                <div className="review-step__name">{configStep.name || '--'}</div>
                <div className="review-step__tags-row">
                    <Tag className={`is-round text-11 ${isLive ? 'is-warning' : 'is-success'}`}>
                        {isLive ? '实盘' : '模拟盘'}
                    </Tag>
                    <Tag className={`is-round text-11 ${configStep.market === 'HK' ? 'is-purple' : 'is-success'}`}>
                        {{ HK: '港股', CN: 'A股', US: '美股' }[configStep.market] ?? configStep.market}
                    </Tag>
                    {configStep.benchmark && (
                        <Tag className="is-round text-11 border-tag">
                            基准：{benchmarkLabel[configStep.benchmark] ?? configStep.benchmark}
                        </Tag>
                    )}
                </div>
                <Row label="交易市场" value={
                    <strong>{{ HK: '港股 (HK)', CN: 'A股 (CN)', US: '美股 (US)' }[configStep.market] ?? configStep.market}</strong>
                } />
                <Row label="建仓开始时间" value={configStep.startDate || '--'} />
                <Row label="初始资金" value={<strong>{configStep.cash.toLocaleString()} 万元</strong>} />
                <Row label="股票池" value={
                    configStep.stockPoolName
                        ? <Tag color="blue" style={{ borderColor: 'var(--accent)' }}>{configStep.stockPoolName}</Tag>
                        : <span style={{ color: 'var(--text-3)' }}>未设置</span>
                } />
                {configStep.description && (
                    <Row label="组合描述" value={configStep.description} />
                )}
            </div>

            {/* 策略配置 */}
            <div className="review-step__section">
                <div className="review-step__section-title">策略配置</div>
                <Row label="组合经理" value={
                    <span>
                        <strong>{manager?.nickname}</strong>
                        <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 8 }}>
                            {manager?.style} · {manager?.risk_level}
                        </span>
                    </span>
                } />
                {strategyVersion && (
                    <>
                        <Row label="策略版本" value={
                            <span>
                                <span style={{ color: 'var(--accent)', fontWeight: 700, marginRight: 6 }}>
                                    {strategyVersion.version_no}
                                </span>
                                {strategyVersion.name}
                                <span style={{ marginLeft: 8, color: 'var(--text-3)', fontSize: 12 }}>
                                    {strategyVersion.status === 'active' ? '已激活' : strategyVersion.status === 'archived' ? '已归档' : strategyVersion.status}
                                </span>
                            </span>
                        } />
                        {strategyVersion.constraints?.length > 0 && (
                            <Row label="策略约束" value={
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                    {strategyVersion.constraints.map(c => (
                                        <Tag key={c} className="border-tag text-11 is-round-tag">{c}</Tag>
                                    ))}
                                </div>
                            } />
                        )}
                    </>
                )}
            </div>

            {/* 风控约束 */}
            <div className="review-step__section">
                <div className="review-step__section-title">风控约束</div>
                <div className="review-step__grid review-step__grid--4">
                    <KV label="单股仓位上限" value={`${configStep.maxSingleWeight ?? 15}%`} />
                    <KV label="行业仓位上限" value={`${configStep.sectorLimit ?? 40}%`} />
                    <KV label="最大回撤止损" value={`${configStep.maxDrawdown ?? 20}%`} accent />
                    <KV label="最低现金留存" value={`${configStep.cashReserve ?? 5}%`} />
                </div>
            </div>

            {/* AI 授权范围 + 外部信号响应 并排 */}
            <div className="review-step__row-sections">
                <div className="review-step__section review-step__section--flex">
                    <div className="review-step__section-title">
                        <RobotOutlined style={{ marginRight: 6 }} />AI 授权范围
                    </div>
                    <div className="review-step__grid review-step__grid--2">
                        <KV label="最大持仓数量" value={`${configStep.maxPositions} 只`} />
                        <KV label="仓位分配方式" value={positionSizingLabel[configStep.positionSizing] ?? configStep.positionSizing} />
                        <KV label="AI 决策频率" value={rebalanceFrequencyLabel[configStep.rebalanceFrequency] ?? configStep.rebalanceFrequency} />
                    </div>
                </div>

                <div className="review-step__section review-step__section--flex">
                    <div className="review-step__section-title">外部信号响应</div>
                    {[
                        { label: '智能监控信号',    on: configStep.useMonitor },
                        { label: '投研 Agent 信号', on: configStep.useResearch },
                        { label: '分红再投资',       on: configStep.reinvestDividend },
                    ].map(({ label, on }) => (
                        <Row key={label} label={label} value={
                            <Tag className={`is-round text-11 ${on ? 'is-success' : ''}`}
                                style={{ opacity: on ? 1 : 0.45 }}>
                                {on ? '已启用' : '未启用'}
                            </Tag>
                        } />
                    ))}
                </div>
            </div>

            {/* 策略 Skill 参数 */}
            <div className="review-step__section">
                <div className="review-step__section-title">
                    <RobotOutlined style={{ marginRight: 6 }} />策略 Skill 参数
                </div>
                {configStep.strategyParams.length > 0 ? (
                    <div className="review-step__grid review-step__grid--4">
                        {configStep.strategyParams.map(p => (
                            <KV key={p.key} label={p.name} value={formatParam(p)} />
                        ))}
                    </div>
                ) : (
                    <Row label="策略 Skill 参数" value={
                        <span style={{ color: 'var(--text-3)' }}>未配置</span>
                    } />
                )}
            </div>

        </section>
    )
}
